import React, {
  useMemo,
  useState,
  useCallback,
  memo,
  useRef,
  useEffect,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Folder,
  Plus,
  ChevronDown,
  ChevronRight,
  Lock,
  CalendarDays,
  GraduationCap,
  Briefcase,
  Building2,
  Users,
  Lightbulb,
  Microscope,
  Activity,
  Plane,
  Film,
  Home,
  Utensils,
  Laptop,
  Trophy,
  ShoppingCart,
  Target,
  UsersRound,
  FolderSymlink,
  Scale,
  ClipboardList,
  BarChart3,
  Video,
  Youtube,
  Code,
  DollarSign,
  BarChart,
  Settings,
  PhoneCall,
  Layout,
  FileText,
  Mic2,
  Music2,
  Radio,
  Headphones,
  Speaker,
  Podcast,
  Loader,
  MoreVertical,
  Sparkles,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Button } from '../../../components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../components/ui/collapsible';
import { cn } from '../../../lib/utils';
import { useSidebar } from '../../../components/ui/sidebar';
import type { RootState } from '../../../reducers';
import { generateFolderTree } from '../../library/folders/folderHelper';
import { FolderType, FileType } from '../../../types';
import { getFolderUrl } from '../../../lib/routingUtils';
import { RouteParams } from '../../library/types';
import FolderMenu from './FolderMenu';
import FolderIcon from '../../library/folders/actions/FolderIcon';
import FolderAccessModal from '../../library/folders/actions/FolderAccessModal';
import FolderModal from '../../library/folders/FolderModal';
import SharedWithMeModal from '../../shared/SharedWithMeModal';
import { pushModal } from '../../globalModals/globalModalsSlice';
import {
  folderActions,
  triggerFileListRefresh,
} from '../../library/librarySlice';
// import useLimits from '../../plan/hooks/limits'; // Not currently used
import { setDrawerState } from '../dashboardSlice';
import useUserTags from '../../../hooks/useTags';
import { moveFile } from '../../selectedVideo/selectedVideoSlice';

// Icon mapping for custom folder icons
const ICON_MAP = {
  Folder,
  Video,
  Youtube,
  Mic: Mic2,
  Music: Music2,
  Radio,
  Headphones,
  Speaker,
  Podcast,
  Code,
  DollarSign,
  BarChart,
  Settings,
  PhoneCall,
  Layout,
  FileText,
  CalendarDays,
  GraduationCap,
  Briefcase,
  Building2,
  Users,
  Lightbulb,
  Microscope,
  Activity,
  Plane,
  Film,
  Home,
  Utensils,
  Laptop,
  Trophy,
  ShoppingCart,
  Target,
  UsersRound,
  FolderSymlink,
  Scale,
  ClipboardList,
  BarChart3,
};

// Forward declare ConnectedFolderItem
let ConnectedFolderItem: any;

// Create a context to share rename functionality and loading state
const RenameContext = React.createContext<{
  renamingFolderId: string | null;
  startRename: (folderId: string | null) => void;
  loadingFolderId: string | null;
  setLoadingFolderId: (folderId: string | null) => void;
}>({
  renamingFolderId: null,
  startRename: () => {},
  loadingFolderId: null,
  setLoadingFolderId: () => {},
});

const FolderItem = memo(
  ({
    folder,
    level = 0,
    isSelected,
    isOpen,
    onToggle,
    onSelect,
    drawerOpen,
    isDragging = false,
  }: {
    folder: FolderType;
    level?: number;
    isSelected: boolean;
    isOpen: boolean;
    onToggle: () => void;
    onSelect: () => void;
    drawerOpen: boolean;
    isDragging?: boolean;
  }) => {
    const { t } = useTranslation(); // eslint-disable-line @typescript-eslint/no-unused-vars
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [newName, setNewName] = useState(folder.name);
    const [lastClickTime, setLastClickTime] = useState(0);
    const [isSaving, setIsSaving] = useState(false);
    const [iconDialogOpen, setIconDialogOpen] = useState(false);
    const [accessModalOpen, setAccessModalOpen] = useState(false);
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuTriggerRef = useRef<HTMLButtonElement>(null);
    const {
      renamingFolderId,
      startRename,
      loadingFolderId,
      setLoadingFolderId,
    } = React.useContext(RenameContext);
    const isRenaming = renamingFolderId === folder._id;
    const isLoading = loadingFolderId === folder._id;
    const { team, library } = useSelector((state: RootState) => state);
    const teamId = team.selectedTeam?.teamId || team.teams[0]?.teamId;
    const { getTag } = useUserTags();
    const renameStatus = library.actions?.rename?.status;

    // Sortable setup for drag and drop
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging: isSortableDragging,
    } = useSortable({
      id: folder._id,
      disabled: isRenaming || level > 0, // Only allow dragging top-level folders
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging || isSortableDragging ? 0.4 : 1,
      cursor: level === 0 && !isRenaming ? 'grab' : 'pointer',
    };

    React.useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };

      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Focus input when entering rename mode
    useEffect(() => {
      if (isRenaming && inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      }
    }, [isRenaming]);

    const handleSaveRename = useCallback(() => {
      const trimmedName = newName.trim();
      if (trimmedName && trimmedName !== folder.name) {
        setIsSaving(true);
        dispatch(
          folderActions({
            folderId: folder._id,
            action: 'rename',
            data: { newName: trimmedName },
          }),
        );
      } else {
        setNewName(folder.name);
        startRename(null); // Clear renaming state
      }
    }, [newName, folder, dispatch, startRename]);

    // Handle clicking outside to save
    useEffect(() => {
      if (!isRenaming) return undefined;

      const handleClickOutside = (e: MouseEvent) => {
        if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
          handleSaveRename();
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [isRenaming, handleSaveRename]);

    // Reset name when switching to a different folder's rename
    useEffect(() => {
      if (isRenaming) {
        setNewName(folder.name);
      }
    }, [isRenaming, folder.name]);

    // Watch for rename completion
    useEffect(() => {
      if (isSaving && renameStatus === 'succeeded') {
        setIsSaving(false);
        startRename(null);
      } else if (isSaving && renameStatus === 'failed') {
        setIsSaving(false);
        // Keep in rename mode on failure
      }
    }, [isSaving, renameStatus, startRename]);

    const handleDoubleClick = useCallback(() => {
      if (!drawerOpen) return; // Only allow rename when drawer is open
      startRename(folder._id);
    }, [drawerOpen, startRename, folder._id]);

    const handleContextMenu = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setContextMenuOpen(true);
      // Trigger the dropdown menu programmatically
      setTimeout(() => {
        menuTriggerRef.current?.click();
      }, 0);
    }, []);

    const handleFolderClick = useCallback(() => {
      const now = Date.now();
      const timeDiff = now - lastClickTime;

      // Check for double-click (within 500ms)
      if (timeDiff < 500 && drawerOpen) {
        handleDoubleClick();
        setLastClickTime(0);
        return;
      }

      setLastClickTime(now);

      // Don't navigate if we're renaming
      if (isRenaming) return;

      // Only set loading state if we're navigating to a different folder
      if (!isSelected) {
        setLoadingFolderId(folder._id);
      }

      if (folder.children && folder.children.length > 0) {
        onToggle();
      }
      onSelect();

      const url = getFolderUrl({
        teamId,
        folderId: folder._id,
        withBasePath: false,
      });
      navigate(url);

      if (isMobile) {
        dispatch(setDrawerState('closed'));
      }
    }, [
      folder,
      onToggle,
      onSelect,
      navigate,
      teamId,
      isMobile,
      dispatch,
      isRenaming,
      isSelected,
      lastClickTime,
      drawerOpen,
      handleDoubleClick,
      setLoadingFolderId,
    ]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          handleSaveRename();
        } else if (e.key === 'Escape') {
          setNewName(folder.name);
          startRename(null);
        }
      },
      [handleSaveRename, folder.name, startRename],
    );

    // Get custom icon for this folder
    const folderIconsTag = getTag('folder_icons');
    const folderIcons = folderIconsTag ? JSON.parse(folderIconsTag) : {};
    const iconName = folderIcons[folder._id] || 'Folder';
    const IconComponent = ICON_MAP[iconName as keyof typeof ICON_MAP] || Folder;

    const handleIconClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setIconDialogOpen(true);
    }, []);

    const handleLockClick = useCallback((e: React.MouseEvent) => {
      e.stopPropagation();
      setAccessModalOpen(true);
    }, []);

    // Drag and drop handlers for moving files into folders
    const handleDragEnter = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        console.log('Drag enter folder:', folder.name);
        setIsDragOver(true);
      },
      [folder.name],
    );

    const handleDragOver = useCallback((e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      // Don't log on every dragover to avoid spam
      // console.log('Drag over folder:', folder.name);
      setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Only set isDragOver to false if we're actually leaving the folder element
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const x = e.clientX;
        const y = e.clientY;

        if (
          x < rect.left ||
          x >= rect.right ||
          y < rect.top ||
          y >= rect.bottom
        ) {
          console.log('Drag leave folder:', folder.name);
          setIsDragOver(false);
        }
      },
      [folder.name],
    );

    const handleDrop = useCallback(
      async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        console.log('File dropped on folder:', folder.name);

        try {
          // Get the file data from the drag event
          const fileDataStr = e.dataTransfer.getData('application/json');
          console.log('Dropped file data:', fileDataStr);
          if (!fileDataStr) {
            console.log('No file data found in drop event');
            return;
          }

          const file: FileType = JSON.parse(fileDataStr);
          console.log('Moving file:', file.name, 'to folder:', folder.name);

          // Don't move if it's already in this folder
          if (file.parentId === folder._id) {
            console.log('File already in this folder');
            return;
          }

          // Move the file to this folder
          if (teamId) {
            console.log('Dispatching moveFile action with:', {
              fileId: file._id,
              toTeamId: teamId,
              toFolderId: folder._id,
            });

            dispatch(
              moveFile({
                fileId: file._id,
                toTeamId: teamId,
                toFolderId: folder._id,
              }),
            );

            console.log('File move initiated!');
            // Trigger a refresh of the file list to show the updated state
            dispatch(triggerFileListRefresh());
          } else {
            console.error('No teamId available');
          }
        } catch (error) {
          console.error('Failed to move file:', error);
        }
      },
      [folder._id, folder.name, teamId, dispatch],
    );

    // Check folder permissions or other properties to determine access
    const isRestricted = !!folder.permissions;
    const hasChildren = folder.children && folder.children.length > 0;

    return (
      <div className="relative" ref={setNodeRef} style={style}>
        <div
          className="flex items-center"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            opacity: drawerOpen ? 1 : 0,
            transition: 'opacity 100ms ease-in-out',
            pointerEvents: drawerOpen ? 'auto' : 'none',
          }}
        >
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'w-full rounded-md relative transition-all duration-200',
              'justify-start gap-3',
              'text-gray-500 dark:text-gray-400 [&_svg]:text-gray-500 dark:[&_svg]:text-gray-400',
              'hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-700 dark:hover:text-gray-200 hover:[&_svg]:text-gray-700 dark:hover:[&_svg]:text-gray-200',
              level > 0 && 'ml-4',
              isRenaming && 'pr-2',
              !isRenaming && 'pr-8',
              isSelected &&
                'bg-gray-100 dark:bg-muted text-gray-900 dark:text-gray-100 [&_svg]:text-gray-900 dark:[&_svg]:text-gray-100',
              level === 0 &&
                !isRenaming &&
                drawerOpen &&
                'active:cursor-grabbing',
              isDragOver && 'bg-primary/20 ring-2 ring-inset ring-primary',
            )}
            onClick={!isRenaming ? handleFolderClick : undefined}
            onDoubleClick={!isRenaming ? handleDoubleClick : undefined}
            onContextMenu={handleContextMenu}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            {...(level === 0 && !isRenaming && drawerOpen ? attributes : {})}
            {...(level === 0 && !isRenaming && drawerOpen ? listeners : {})}
          >
            {hasChildren && !isRenaming && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                onPointerDown={(e) => {
                  // Prevent drag when clicking on chevron
                  e.stopPropagation();
                }}
                className="flex items-center -ml-1 bg-transparent border-none p-0 cursor-pointer z-10"
                aria-label={isOpen ? 'Collapse folder' : 'Expand folder'}
              >
                {isOpen ? (
                  <ChevronDown
                    className="h-4 w-4 text-inherit"
                    strokeWidth={1.75}
                  />
                ) : (
                  <ChevronRight
                    className="h-4 w-4 text-inherit"
                    strokeWidth={1.75}
                  />
                )}
              </button>
            )}
            {isRenaming || isSaving ? (
              <div className="flex items-center gap-3 w-full">
                {isSaving ? (
                  <Loader
                    className="h-4 w-4 flex-shrink-0 animate-spin"
                    strokeWidth={1.75}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={handleIconClick}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="flex-shrink-0 p-0 border-0 bg-transparent hover:bg-gray-100 dark:hover:bg-muted rounded-sm transition-all duration-200 z-10"
                    title="Change icon"
                  >
                    <IconComponent
                      className="h-4 w-4 text-inherit"
                      strokeWidth={1.75}
                    />
                  </button>
                )}
                {isSaving ? (
                  <span className="flex-1 text-left text-sm truncate">
                    {newName}
                  </span>
                ) : (
                  <input
                    ref={inputRef}
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-1 text-left bg-transparent border-0 outline-none focus:outline-none p-0 truncate text-gray-500 dark:text-gray-400"
                    style={{
                      fontSize: '0.875rem',
                      lineHeight: '1.25rem',
                      fontFamily: 'inherit',
                    }}
                    maxLength={50}
                    disabled={isSaving}
                  />
                )}
              </div>
            ) : (
              <>
                {isLoading ? (
                  <Loader
                    className="h-4 w-4 flex-shrink-0 animate-spin"
                    strokeWidth={1.75}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={handleIconClick}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="flex-shrink-0 p-0 border-0 bg-transparent hover:bg-gray-100 dark:hover:bg-muted rounded-sm transition-all duration-200 z-10"
                    title="Change icon"
                  >
                    <IconComponent className="h-4 w-4" strokeWidth={1.75} />
                  </button>
                )}
                <span className="flex-1 truncate text-left text-sm text-inherit">
                  {folder.name}
                </span>
              </>
            )}
            {!isRenaming && !isSaving && (
              <div
                className={cn(
                  'absolute right-1 top-1/2 -translate-y-1/2 z-10 transition-opacity flex items-center gap-1',
                  isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none',
                )}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                role="presentation"
              >
                {isRestricted && (
                  <button
                    type="button"
                    onClick={handleLockClick}
                    className="flex items-center p-0.5 hover:bg-gray-100 dark:hover:bg-muted rounded-sm transition-all duration-200"
                    title="Manage folder access"
                  >
                    <Lock
                      className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500"
                      strokeWidth={1.75}
                    />
                  </button>
                )}
                <FolderMenu
                  folder={folder}
                  onRename={() => startRename(folder._id)}
                  triggerRef={menuTriggerRef}
                  open={contextMenuOpen}
                  onOpenChange={setContextMenuOpen}
                />
              </div>
            )}
          </Button>
        </div>

        {iconDialogOpen && (
          <FolderIcon
            open={iconDialogOpen}
            folderId={folder._id}
            currentIcon={iconName}
            onClose={() => setIconDialogOpen(false)}
          />
        )}

        {accessModalOpen && (
          <FolderAccessModal
            open={accessModalOpen}
            folder={folder}
            onClose={() => setAccessModalOpen(false)}
          />
        )}

        {hasChildren && isOpen && (
          <div className="mt-1">
            {folder.children?.map((childFolder) => (
              <ConnectedFolderItem
                key={childFolder._id}
                folder={childFolder}
                level={level + 1}
                drawerOpen={drawerOpen}
              />
            ))}
          </div>
        )}
      </div>
    );
  },
);

FolderItem.displayName = 'FolderItem';

// Now define ConnectedFolderItem after FolderItem
ConnectedFolderItem = memo(
  ({
    folder,
    level,
    drawerOpen,
    isDragging = false,
  }: {
    folder: FolderType;
    level: number;
    drawerOpen: boolean;
    isDragging?: boolean;
  }) => {
    const { folderId } = useParams<RouteParams>();
    const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());

    const isSelected = folderId === folder._id;
    const isOpen = openFolders.has(folder._id);

    const handleToggle = useCallback(() => {
      setOpenFolders((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(folder._id)) {
          newSet.delete(folder._id);
        } else {
          newSet.add(folder._id);
        }
        return newSet;
      });
    }, [folder._id]);

    const handleSelect = useCallback(() => {
      // Handle selection logic if needed
    }, []);

    return (
      <FolderItem
        folder={folder}
        level={level}
        isSelected={isSelected}
        isOpen={isOpen}
        onToggle={handleToggle}
        onSelect={handleSelect}
        drawerOpen={drawerOpen}
        isDragging={isDragging}
      />
    );
  },
);

ConnectedFolderItem.displayName = 'ConnectedFolderItem';

const FolderTree = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { library, team } = useSelector((state: RootState) => state);
  const { state, isMobile: isMobileFromSidebar, openMobile } = useSidebar();
  const drawerOpen = isMobileFromSidebar ? openMobile : state === 'expanded';
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [loadingFolderId, setLoadingFolderId] = useState<string | null>(null);
  const [isSectionOpen, setIsSectionOpen] = useState(() => {
    // Load saved state from localStorage
    const saved = localStorage.getItem('folderSectionOpen');
    return saved !== null ? saved === 'true' : true;
  });
  const [isHeaderHovered, setIsHeaderHovered] = useState(false);
  const [isPrivateHovered, setIsPrivateHovered] = useState(false);
  const [showPrivateAskAI, setShowPrivateAskAI] = useState(false);
  const [showSharedWithMeModal, setShowSharedWithMeModal] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const { getTag, setTag } = useUserTags();
  const { folderId } = useParams<RouteParams>();
  const teamId = team.selectedTeam?.teamId || team.teams[0]?.teamId;
  // const guardLimit = useLimits(); // Not currently used

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Clear loading state when files finish loading
  React.useEffect(() => {
    if (
      library.files.status === 'succeeded' ||
      library.files.status === 'failed'
    ) {
      setLoadingFolderId(null);
    }
  }, [library.files.status]);

  // Sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: isMobile ? 10 : 8, // Require more movement on mobile
        delay: isMobile ? 250 : 150, // Add delay on both mobile and desktop to prevent accidental drags
        tolerance: isMobile ? 5 : 0, // Allow 5px movement tolerance on mobile
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const folders = useMemo(() => {
    // Check if library.folders has a data property
    const folderData = (library.folders as any)?.data || library.folders || [];
    const tree = generateFolderTree(
      Array.isArray(folderData) ? folderData : [],
    );

    // Get folder order from tags
    const folderOrderTag = getTag('folder-order' as any);
    const folderOrder = folderOrderTag
      ? JSON.parse(folderOrderTag as string)
      : null;

    if (folderOrder && Array.isArray(folderOrder)) {
      // Sort folders based on saved order
      const orderMap = new Map(
        folderOrder.map((id: string, index: number) => [id, index]),
      );
      tree.sort((a, b) => {
        const aIndex = orderMap.get(a._id) ?? Infinity;
        const bIndex = orderMap.get(b._id) ?? Infinity;
        return aIndex - bIndex;
      });
    }

    return tree;
  }, [library.folders, getTag]);

  const handleCreateFolder = useCallback(() => {
    // Temporarily disable folder limit check due to type issues
    // if (!guardLimit('folders')) return;
    dispatch(pushModal({ type: 'create-folder-modal' as any }));
  }, [dispatch]);

  const handleToggleSection = useCallback(() => {
    setIsSectionOpen((prev) => {
      const newState = !prev;
      localStorage.setItem('folderSectionOpen', String(newState));
      return newState;
    });
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = folders.findIndex((f) => f._id === active.id);
      const newIndex = folders.findIndex((f) => f._id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create new order array
        const newOrder = [...folders];
        const [movedFolder] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, movedFolder);

        // Save new order to tags
        const orderIds = newOrder.map((f) => f._id);
        setTag({
          key: 'folder-order' as any,
          value: JSON.stringify(orderIds),
        });
      }
    }

    setActiveId(null);
  };

  const activeFolder = activeId
    ? folders.find((f) => f._id === activeId)
    : null;

  return (
    <RenameContext.Provider
      value={{
        renamingFolderId,
        startRename: setRenamingFolderId,
        loadingFolderId,
        setLoadingFolderId,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="space-y-1">
          <TooltipProvider>
            <Collapsible open={isSectionOpen} onOpenChange={setIsSectionOpen}>
              {/* Header with Create Folder button and collapse toggle */}
              <div
                className="flex items-center justify-between mb-1 h-6"
                style={{
                  opacity: drawerOpen ? 1 : 0,
                  transition: 'opacity 100ms ease-in-out',
                }}
                onMouseEnter={() => setIsHeaderHovered(true)}
                onMouseLeave={() => setIsHeaderHovered(false)}
              >
                <CollapsibleTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex items-center gap-1 px-2 h-6 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-700 dark:hover:text-gray-200 rounded-md transition-all duration-200"
                    onClick={handleToggleSection}
                  >
                    <span className="text-xs font-medium text-muted-foreground dark:text-muted-foreground">
                      {t('folders.title', 'Folders')}
                    </span>
                    <ChevronRight
                      className={cn(
                        'h-3 w-3 transition-all duration-200',
                        isSectionOpen && 'rotate-90',
                        !isHeaderHovered && 'opacity-0',
                      )}
                      strokeWidth={1.75}
                    />
                  </Button>
                </CollapsibleTrigger>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-md h-6 w-6 p-0 mr-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-700 dark:hover:text-gray-200 transition-all duration-200"
                      onClick={handleCreateFolder}
                    >
                      <Plus className="h-4 w-4" strokeWidth={1.75} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side={drawerOpen ? 'top' : 'right'}>
                    {t('folders.create', 'Create Folder')}
                  </TooltipContent>
                </Tooltip>
              </div>

              {/* Folder List - collapsible */}
              <CollapsibleContent className="space-y-0.5">
                {/* Private Folder - Special folder (My Recordings) */}
                <div
                  className="relative"
                  onMouseEnter={() => setIsPrivateHovered(true)}
                  onMouseLeave={() => setIsPrivateHovered(false)}
                  style={{
                    opacity: drawerOpen ? 1 : 0,
                    transition: 'opacity 100ms ease-in-out',
                    pointerEvents: drawerOpen ? 'auto' : 'none',
                  }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'w-full rounded-md flex items-center justify-start gap-3 px-3 transition-all duration-200',
                      'text-gray-500 dark:text-gray-400 [&_svg]:text-gray-500 dark:[&_svg]:text-gray-400',
                      'hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-700 dark:hover:text-gray-200 hover:[&_svg]:text-gray-700 dark:hover:[&_svg]:text-gray-200',
                      folderId === 'private' &&
                        'bg-gray-100 dark:bg-muted text-gray-900 dark:text-gray-100 [&_svg]:text-gray-900 dark:[&_svg]:text-gray-100',
                    )}
                    onClick={() => {
                      // Only set loading state if we're navigating to a different folder
                      if (folderId !== 'private') {
                        setLoadingFolderId('private');
                      }
                      const url = getFolderUrl({
                        teamId,
                        folderId: 'private',
                        withBasePath: false,
                      });
                      navigate(url);
                      if (isMobile) {
                        dispatch(setDrawerState('closed'));
                      }
                    }}
                  >
                    {loadingFolderId === 'private' ? (
                      <Loader
                        className="h-4 w-4 flex-shrink-0 animate-spin"
                        strokeWidth={1.75}
                      />
                    ) : (
                      <Lock
                        className="h-4 w-4 flex-shrink-0"
                        strokeWidth={1.75}
                      />
                    )}
                    <span className="flex-1 truncate text-left text-sm">
                      {t('drawer.quickActions.private.label')}
                    </span>
                  </Button>
                  {isPrivateHovered && (
                    <div className="absolute right-1 top-1/2 -translate-y-1/2 z-10">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                            }}
                          >
                            <MoreVertical
                              className="h-4 w-4"
                              strokeWidth={1.75}
                            />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPrivateAskAI(true);
                            }}
                          >
                            <Sparkles
                              className="mr-2 h-4 w-4"
                              strokeWidth={1.75}
                            />
                            {t('library.folder.actions.askAI', 'Ask AI')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>

                {/* Shared with me - Special folder */}
                <div
                  className="relative"
                  style={{
                    opacity: drawerOpen ? 1 : 0,
                    transition: 'opacity 100ms ease-in-out',
                    pointerEvents: drawerOpen ? 'auto' : 'none',
                  }}
                >
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'w-full rounded-md flex items-center justify-start gap-3 px-3 transition-all duration-200',
                      'text-gray-500 dark:text-gray-400 [&_svg]:text-gray-500 dark:[&_svg]:text-gray-400',
                      'hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-700 dark:hover:text-gray-200 hover:[&_svg]:text-gray-700 dark:hover:[&_svg]:text-gray-200',
                    )}
                    onClick={() => {
                      setShowSharedWithMeModal(true);
                      if (isMobile) {
                        dispatch(setDrawerState('closed'));
                      }
                    }}
                  >
                    <FolderSymlink
                      className="h-4 w-4 flex-shrink-0"
                      strokeWidth={1.75}
                    />
                    <span className="flex-1 truncate text-left text-sm">
                      {t('drawer.quickActions.shared.label', 'Shared with me')}
                    </span>
                  </Button>
                </div>

                <SortableContext
                  items={folders.map((f) => f._id)}
                  strategy={verticalListSortingStrategy}
                >
                  {folders.map((folder) => (
                    <ConnectedFolderItem
                      key={folder._id}
                      folder={folder}
                      level={0}
                      drawerOpen={drawerOpen}
                      isDragging={folder._id === activeId}
                    />
                  ))}
                </SortableContext>
              </CollapsibleContent>
            </Collapsible>
          </TooltipProvider>
          <DragOverlay>
            {activeFolder ? (
              <div className="bg-background border rounded-md shadow-xl">
                <div className="flex items-center gap-3 px-3 py-2">
                  <Folder
                    className="h-4 w-4 text-muted-foreground"
                    strokeWidth={1.75}
                  />
                  <span className="text-sm font-medium">
                    {activeFolder.name}
                  </span>
                </div>
              </div>
            ) : null}
          </DragOverlay>
        </div>
      </DndContext>
      {showPrivateAskAI && (
        <FolderModal
          open={showPrivateAskAI}
          onClose={() => setShowPrivateAskAI(false)}
          folder={{
            _id: 'private',
            name: 'Private',
            type: 'Folder',
            id: 'private',
            provider: 'S3:team',
            createdAt: new Date(),
            parentId: null,
            children: [],
            permissions: [],
          }}
        />
      )}
      {showSharedWithMeModal && (
        <SharedWithMeModal
          open={showSharedWithMeModal}
          onClose={() => setShowSharedWithMeModal(false)}
        />
      )}
    </RenameContext.Provider>
  );
};

export default memo(FolderTree);
